import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useServices } from '../../../../hooks/useServices';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAppointmentsOwner } from '../../../../hooks/useAppointmentsOwner';
import { useStaff } from '../../../../hooks/useStaff';
import { useCategories } from '../../../../hooks/useCategories';
import { useToast } from '../../../../contexts/ToastContext';
import { CategoryListResponse, Category } from '../../../../types/api-responses';
import InputField from '../../../../components/InputField/InputField';
import Toggle from '../../../../components/Toggle/Toggle';
import Button from '../../../../components/Button/Button';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import TextArea from '../../../../components/TextArea/TextArea';
import Calendar from '../../../../components/Calendar/Calendar';
import TimePicker from '../../../../components/TimePicker/TimePicker';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock';
import AppointmentOptionPicker from '../../../../components/AppointmentOptionPicker/AppointmentOptionPicker';
import { isServiceOptionGroupsEnabled } from '../../../../lib/serviceOptionGroups/serviceOptionGroupsFlag';
import {
    buildOptionSelectionPayload,
    computeSelectionTotals,
    defaultSelection,
    deriveLegacyPrefillPricingOptionId,
    deriveSelectionFromDetail,
    emptySelection,
    hasBookableOptionGroups,
    matchLegacyPricingOptionId,
    validateSelection,
    type AppointmentOptionSelection,
} from '../../../../lib/serviceOptionGroups/appointmentOptionSelection';
import type { ServiceOptionGroupDto } from '../../../../types/serviceOptionGroups';
import type { AppointmentSelectedOptionLineDto } from '../../../../services/api';
import styles from './AddEditAppointment.module.css';

enum AppointmentStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    NoShow = 'NoShow'
}

enum PaymentStatus {
    Paid = 'Paid',
    Unpaid = 'Unpaid',
    Upcoming = 'Upcoming',
    Draft = 'Draft',
    Late = 'Late'
}

interface AddEditAppointmentProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    selectedTime: string;
    selectedDate: string;
    editingAppointmentId?: string | null;
    businessId?: number;
}

const AddEditAppointment: React.FC<AddEditAppointmentProps> = ({
    onClose,
    onSuccess,
    isOpen,
    selectedTime,
    selectedDate,
    editingAppointmentId,
    businessId = 0
}) => {
    const { services, getServices, loading: servicesLoading } = useServices();
    const { createAppointment, getAppointmentById, updateAppointment, loading: appointmentLoading } = useAppointmentsOwner();
    const { staff, getBusinessStaff, loading: staffLoading } = useStaff();
    const { getBusinessCategories, loading: categoriesLoading } = useCategories();
    const { showToast } = useToast();
    const { t } = useLanguage();

    // PBI 1962 (AB#1962) — owner manual-booking option picker rollout flag
    // (same env flag as the Phase-3 service builder). Flag OFF ⇒ exactly
    // today's modal; option state stays inert and the payload keys are omitted.
    const optionGroupsEnabled = useMemo(() => isServiceOptionGroupsEnabled(), []);

    const [isDirty, setIsDirty] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [categories, setCategories] = useState<Array<{ id: number, name: string }>>([]);
    const [filteredServices, setFilteredServices] = useState<Array<any>>([]);
    const [pricingOptions, setPricingOptions] = useState<Array<{ id: number, name: string, price: number, currency: string, duration: string }>>([]);

    const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date | undefined>(undefined);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');

    const [appointmentData, setAppointmentData] = useState({
        businessId: businessId,
        clientName: '',
        mobileNumber: '',
        email: '',
        categoryId: 0,
        serviceId: 0,
        pricingOptionId: 0,
        amount: '',
        staffId: 0,
        paymentStatus: PaymentStatus.Upcoming,
        appointmentStatus: AppointmentStatus.Pending,
        appointmentDate: new Date().toISOString(),
        isDraft: false,
        notes: ''
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // PBI 1962 — option-group selection for the currently selected service.
    // `editSelectedOptions` holds the appointment detail's itemized lines so
    // the picker can pre-select them once the service's groups are known.
    const [optionSelection, setOptionSelection] = useState<AppointmentOptionSelection>(emptySelection());
    const [editSelectedOptions, setEditSelectedOptions] = useState<AppointmentSelectedOptionLineDto[] | null>(null);
    // AB#1970 — the children-first legacy prefill runs exactly once per load
    // (see effect below); it must never clobber a later manual change.
    const legacyPrefillDerivedRef = useRef(false);

    const paymentStatusOptions = [
        { label: 'Paid', value: PaymentStatus.Paid },
        { label: 'Unpaid', value: PaymentStatus.Unpaid },
        { label: 'Upcoming', value: PaymentStatus.Upcoming },
        { label: 'Draft', value: PaymentStatus.Draft },
        { label: 'Late', value: PaymentStatus.Late }
    ];

    const appointmentStatusOptions = [
        { label: 'Pending', value: AppointmentStatus.Pending },
        { label: 'Approved', value: AppointmentStatus.Approved },
        { label: 'Completed', value: AppointmentStatus.Completed },
        { label: 'Cancelled', value: AppointmentStatus.Cancelled },
        { label: 'No Show', value: AppointmentStatus.NoShow }
    ];

    // PBI 1962 — the selected service's bookable option groups. Feature
    // detection is DTO-driven: the backend only serializes `optionGroups`
    // when its `service_option_groups` kill-switch is ON, so absent/empty
    // groups (or FE flag OFF) keep the legacy pricing-option dropdown.
    const activeOptionGroups: ServiceOptionGroupDto[] | null = useMemo(() => {
        if (!optionGroupsEnabled || appointmentData.serviceId <= 0) return null;
        const service = services.find(s => s.id === appointmentData.serviceId);
        const groups = service?.optionGroups;
        return hasBookableOptionGroups(groups) ? groups : null;
    }, [optionGroupsEnabled, appointmentData.serviceId, services]);

    const optionTotals = useMemo(
        () => (activeOptionGroups ? computeSelectionTotals(activeOptionGroups, optionSelection) : null),
        [activeOptionGroups, optionSelection],
    );

    const resetForm = useCallback(() => {
        setAppointmentData({
            businessId: businessId,
            clientName: '',
            mobileNumber: '',
            email: '',
            categoryId: 0,
            serviceId: 0,
            pricingOptionId: 0,
            amount: '',
            staffId: 0,
            paymentStatus: PaymentStatus.Upcoming,
            appointmentStatus: AppointmentStatus.Pending,
            appointmentDate: new Date().toISOString(),
            isDraft: false,
            notes: ''
        });
        setErrors({});
        setSelectedTimeSlot('');
        setCalendarSelectedDate(undefined);
        setOptionSelection(emptySelection());
        setEditSelectedOptions(null);
        legacyPrefillDerivedRef.current = false;
    }, [businessId]);

    const loadExistingAppointment = useCallback(async (appointmentId: string) => {
        try {
            setIsEditMode(true);
            legacyPrefillDerivedRef.current = false;
            const appointment = await getAppointmentById(parseInt(appointmentId));

            if (process.env.NODE_ENV === 'development') console.log('Loaded appointment for editing:', appointment);

            // Map the appointment data to form structure
            setAppointmentData({
                businessId: appointment.businessId || businessId,
                clientName: appointment.clientName || appointment.customerName || '',
                mobileNumber: appointment.clientPhone || appointment.phoneNumber || '',
                email: appointment.clientEmail || appointment.email || '',
                categoryId: appointment.categoryId || 0,
                serviceId: appointment.businessServiceId || appointment.serviceId || 0,
                // M57 REMOVAL POINT (AB#1970) — the scalar `servicePricingOptionId`
                // is only a provisional seed here for flag-OFF bookings that have
                // no `selectedOptions[]`. When the detail carries children, the
                // effect below re-derives this value children-first once the
                // service's pricing options load. Drop the scalar read (and the
                // fallback in `deriveLegacyPrefillPricingOptionId`) with the M57
                // column drop.
                pricingOptionId: appointment.servicePricingOptionId || appointment.pricingOptionId || 0,
                amount: appointment.amount?.toString() || appointment.servicePrice?.toString() || '',
                staffId: appointment.staffId || 0,
                paymentStatus: ((appointment.paymentStatus as number) || PaymentStatus.Upcoming) as PaymentStatus,
                appointmentStatus: (appointment.status as AppointmentStatus) || AppointmentStatus.Pending,
                appointmentDate: appointment.appointmentDate || new Date().toISOString(),
                isDraft: appointment.isDraft || false,
                notes: appointment.notes || ''
            });

            // PBI 1962 — keep the detail's itemized option lines so the picker
            // can pre-select them once the service's groups are available.
            setEditSelectedOptions(appointment.selectedOptions ?? null);

            // Set calendar date
            if (appointment.appointmentDate) {
                // Date from backend is ISO 8601 with Z (UTC)
                // new Date() parses as UTC, then getHours/getDate auto-convert to local
                const appointmentDate = new Date(appointment.appointmentDate);
                setCalendarSelectedDate(appointmentDate);

                // Set time - auto-converts from UTC to local timezone
                const timeString = appointmentDate.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
                setSelectedTimeSlot(timeString);
            }

        } catch (error) {
            console.error('Failed to load appointment for editing:', error);
            // Fall back to empty form
            setIsEditMode(true);
        }
    }, [getAppointmentById, businessId]);

    const fetchInitialData = useCallback(async () => {
        try {
            // Load all data in parallel
            const promises = [];

            if (services.length === 0) {
                promises.push(getServices());
            }

            if (staff.length === 0) {
                promises.push(getBusinessStaff({ businessId }));
            }

            promises.push(getBusinessCategories());

            const results = await Promise.all(promises);

            // Set categories from the last promise result
            const categoriesResponse = results[results.length - 1] as CategoryListResponse;
            if (categoriesResponse?.categories) {
                setCategories(categoriesResponse.categories.map((cat: Category) => ({
                    id: cat.id,
                    name: cat.name
                })));
            }

        } catch (error) {
            console.error('Failed to fetch initial data:', error);
        }
    }, [getServices, getBusinessStaff, getBusinessCategories, services.length, staff.length, businessId]);

    useBodyScrollLock(isOpen);

    useEffect(() => {
        if (isOpen) {
            const initializeModal = async () => {
                setIsInitialLoading(true);
                try {
                    // First, load all the initial data (categories, services, staff)
                    await fetchInitialData();

                    // Then check if we're editing or creating
                    if (editingAppointmentId) {
                        await loadExistingAppointment(editingAppointmentId);
                    } else {
                        setIsEditMode(false);
                        resetForm();

                        // Initialize calendar date from selectedDate prop
                        try {
                            if (selectedDate && selectedDate.includes('/')) {
                                const [day, month, year] = selectedDate.split('/').map(Number);
                                const dateObj = new Date(year, month - 1, day);
                                setCalendarSelectedDate(dateObj);
                            }
                        } catch (error) {
                            console.error('Error parsing initial date:', error);
                            setCalendarSelectedDate(new Date());
                        }

                        // Initialize time from selectedTime prop
                        if (selectedTime && selectedTime.includes(':')) {
                            const [hours, minutes] = selectedTime.split(':').map(Number);
                            const amPm = hours >= 12 ? 'PM' : 'AM';
                            const displayHour = hours % 12 || 12;
                            const displayMinute = minutes.toString().padStart(2, '0');
                            const timeString = `${displayHour}:${displayMinute} ${amPm}`;
                            setSelectedTimeSlot(timeString);
                        }
                    }
                } finally {
                    setIsInitialLoading(false);
                }
            };

            initializeModal();
        }
    }, [isOpen, editingAppointmentId, fetchInitialData, resetForm, loadExistingAppointment, selectedDate, selectedTime]);

    const updateAppointmentDateTime = useCallback((date?: Date, time?: string) => {
        if (!date) return;

        // Salon owner (in Egypt) selects local time
        // Example: Selects 10 AM Egypt time (EET UTC+2)
        const appointmentDate = new Date(date);

        if (time && time.includes(':')) {
            const [timePart, period] = time.split(' ');
            const [hours, minutes] = timePart.split(':').map(Number);

            let finalHours = hours;
            if (period === 'PM' && hours !== 12) {
                finalHours += 12;
            } else if (period === 'AM' && hours === 12) {
                finalHours = 0;
            }

            // setHours() sets time in local timezone (Egypt time: 10 AM)
            appointmentDate.setHours(finalHours, minutes, 0, 0);
        } else {
            const now = new Date();
            appointmentDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
        }

        // toISOString() converts from local to UTC with Z suffix
        // Result: 10 AM Egypt (UTC+2) → 08:00:00Z (UTC)
        setAppointmentData(prev => ({
            ...prev,
            appointmentDate: appointmentDate.toISOString()
        }));
    }, []);

    const handleCalendarDateChange = useCallback((date: Date) => {
        setCalendarSelectedDate(date);
        updateAppointmentDateTime(date, selectedTimeSlot);
    }, [selectedTimeSlot, updateAppointmentDateTime]);

    const handleTimeChange = useCallback((time: string) => {
        setSelectedTimeSlot(time);
        updateAppointmentDateTime(calendarSelectedDate, time);
    }, [calendarSelectedDate, updateAppointmentDateTime]);

    // Filter services when category changes
    useEffect(() => {
        if (appointmentData.categoryId > 0 && services.length > 0 && categories.length > 0) {
            const selectedCategory = categories.find(c => c.id === appointmentData.categoryId);

            if (selectedCategory) {
                const servicesForCategory = services.filter(service =>
                    service.categoryName.toLowerCase() === selectedCategory.name.toLowerCase()
                );

                setFilteredServices(servicesForCategory);
                if (process.env.NODE_ENV === 'development') console.log('Filtered services for category:', selectedCategory.name, servicesForCategory);
            } else {
                setFilteredServices([]);
            }

            // Only reset service/pricing if not in edit mode or if category actually changed
            if (!isEditMode) {
                setAppointmentData(prev => ({
                    ...prev,
                    serviceId: 0,
                    pricingOptionId: 0,
                    amount: ''
                }));
                setPricingOptions([]);
            }
        } else {
            setFilteredServices([]);
        }
    }, [appointmentData.categoryId, services, categories, isEditMode]);

    // Update pricing options when service changes
    useEffect(() => {
        if (appointmentData.serviceId > 0 && services.length > 0) {
            const selectedService = services.find(s => s.id === appointmentData.serviceId);
            if (selectedService && selectedService.pricingOptions) {
                setPricingOptions(selectedService.pricingOptions.map((opt: any) => ({
                    id: opt.id,
                    name: opt.name,
                    price: opt.price,
                    currency: opt.currency || 'AED',
                    duration: opt.duration
                })));
                if (process.env.NODE_ENV === 'development') console.log('Loaded pricing options for service:', selectedService.name, selectedService.pricingOptions);
            } else {
                setPricingOptions([]);
            }

            // Only reset pricing option if not in edit mode
            if (!isEditMode) {
                setAppointmentData(prev => ({
                    ...prev,
                    pricingOptionId: 0,
                    amount: ''
                }));
            }
        } else {
            setPricingOptions([]);
        }
    }, [appointmentData.serviceId, services, isEditMode]);

    // PBI 1962 / AB#1970 (M57 pre-flight) — children-first prefill for the
    // LEGACY pricing dropdown in edit mode. Once the loaded appointment's
    // service pricing options are available, re-derive `pricingOptionId` from
    // the detail's `selectedOptions[]` variant line (name + price match),
    // keeping the scalar seed only as fallback. One-shot: must not clobber a
    // later manual service/option change by the owner.
    useEffect(() => {
        if (!isEditMode || legacyPrefillDerivedRef.current) return;
        if (!editSelectedOptions || editSelectedOptions.length === 0) return;
        if (pricingOptions.length === 0) return;
        legacyPrefillDerivedRef.current = true;
        setAppointmentData(prev => ({
            ...prev,
            pricingOptionId: deriveLegacyPrefillPricingOptionId(
                editSelectedOptions,
                pricingOptions,
                prev.pricingOptionId,
            ),
        }));
    }, [isEditMode, editSelectedOptions, pricingOptions]);

    // PBI 1962 — seed the option selection whenever the bookable groups for
    // the selected service change (service switched, or the services list
    // arrived). Edit mode pre-selects from the appointment detail's itemized
    // `selectedOptions[]` (only ids that still exist on the service's groups);
    // otherwise the default (sole variant auto-picked, no add-ons).
    useEffect(() => {
        if (!activeOptionGroups) {
            setOptionSelection(emptySelection());
            return;
        }
        setOptionSelection(
            isEditMode
                ? deriveSelectionFromDetail(activeOptionGroups, editSelectedOptions)
                : defaultSelection(activeOptionGroups),
        );
    }, [activeOptionGroups, isEditMode, editSelectedOptions]);

    const handleOptionSelectionChange = useCallback((next: AppointmentOptionSelection) => {
        setIsDirty(true);
        setOptionSelection(next);
    }, []);

    const handleInputChange = (name: string, value: string | number | boolean) => {
        setIsDirty(true);
        setAppointmentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Helper functions for dropdown display values
    const getCategoryDisplayValue = (categoryId: number) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? category.name : '';
    };

    const getServiceDisplayValue = (serviceId: number) => {
        const service = filteredServices.find(s => s.id === serviceId) ||
            services.find(s => s.id === serviceId);
        return service ? service.name : '';
    };

    const getPricingOptionDisplayValue = (pricingOptionId: number) => {
        const option = pricingOptions.find(p => p.id === pricingOptionId);
        return option ? option.name : '';
    };

    const getStaffDisplayValue = (staffId: number) => {
        const member = staff.find(s => s.id === staffId);
        return member ? member.fullName : '';
    };

    const getPaymentStatusDisplayValue = (statusValue: PaymentStatus) => {
        const status = paymentStatusOptions.find(s => s.value === statusValue);
        return status ? status.label : '';
    };

    const getAppointmentStatusDisplayValue = (statusValue: AppointmentStatus) => {
        const status = appointmentStatusOptions.find(s => s.value === statusValue);
        return status ? status.label : '';
    };

    // Change handlers for dropdowns
    const handleCategoryChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedCategory = categories.find(category => category.name === value);
            if (selectedCategory) {
                handleInputChange('categoryId', selectedCategory.id);
            }
        }
    };

    const handleServiceChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedService = filteredServices.find(service => service.name === value);
            if (selectedService) {
                handleInputChange('serviceId', selectedService.id);
            }
        }
    };

    const handlePricingOptionChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedOption = pricingOptions.find(option => option.name === value);
            if (selectedOption) {
                handleInputChange('pricingOptionId', selectedOption.id);
                if (selectedOption.price) {
                    handleInputChange('amount', selectedOption.price.toString());
                }
            }
        }
    };

    const handleStaffChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedStaff = staff.find(s => s.fullName === value);
            if (selectedStaff) {
                handleInputChange('staffId', selectedStaff.id);
            }
        }
    };

    const handlePaymentStatusChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedStatus = paymentStatusOptions.find(status => status.label === value);
            if (selectedStatus) {
                handleInputChange('paymentStatus', selectedStatus.value);
            }
        }
    };

    const handleAppointmentStatusChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            const selectedStatus = appointmentStatusOptions.find(status => status.label === value);
            if (selectedStatus) {
                handleInputChange('appointmentStatus', selectedStatus.value);
            }
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // Client Name validation
        if (!appointmentData.clientName || appointmentData.clientName.trim() === '') {
            newErrors.clientName = 'Client name is required.';
        } else if (!/^[a-zA-Z\s]+$/.test(appointmentData.clientName)) {
            newErrors.clientName = 'Invalid name. Please use alphabetic characters only.';
        }

        // Mobile Number validation
        if (!appointmentData.mobileNumber || appointmentData.mobileNumber.trim() === '') {
            newErrors.mobileNumber = 'Mobile number is required.';
        } else if (!/^\d{10,15}$/.test(appointmentData.mobileNumber)) {
            newErrors.mobileNumber = 'Invalid mobile number. Please enter a valid 10–15 digit number.';
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!appointmentData.email || appointmentData.email.trim() === '') {
            newErrors.email = 'Email Address is required.';
        } else if (!emailRegex.test(appointmentData.email)) {
            newErrors.email = 'Invalid email format. Please enter a valid email address.';
        }

        // Category validation
        if (!appointmentData.categoryId || appointmentData.categoryId <= 0) {
            newErrors.categoryId = 'Please select a category.';
        }

        // Service validation
        if (!appointmentData.serviceId || appointmentData.serviceId <= 0) {
            newErrors.serviceId = 'Please select a service.';
        }

        // Pricing option / option-group validation. With bookable option
        // groups (PBI 1962) the legacy dropdown is replaced by the picker:
        // validate the selection (variant required + add-on min/max) instead.
        if (activeOptionGroups) {
            const issues = validateSelection(activeOptionGroups, optionSelection);
            if (issues.length > 0) {
                const first = issues[0];
                newErrors.optionSelection = t(first.key)
                    .replace('{n}', String(first.n ?? ''))
                    .replace('{group}', first.group ?? '');
            }
        } else if (!appointmentData.pricingOptionId || appointmentData.pricingOptionId <= 0) {
            newErrors.pricingOptionId = 'Please select a pricing option.';
        }

        // Appointment Time validation (#1198)
        if (!selectedTimeSlot || selectedTimeSlot.trim() === '') {
            newErrors.appointmentTime = 'Appointment time is required.';
        }

        // Amount validation (#1198: better error for negative amounts).
        // With option groups the amount is DERIVED from the selection (and the
        // server total is authoritative), so the manual checks don't apply.
        if (activeOptionGroups) {
            // no-op — amount comes from computeSelectionTotals
        } else if (!appointmentData.amount || appointmentData.amount.trim() === '') {
            newErrors.amount = 'Amount is required.';
        } else if (isNaN(parseFloat(appointmentData.amount))) {
            newErrors.amount = 'Invalid amount. Please enter a valid number.';
        } else if (parseFloat(appointmentData.amount) < 0) {
            newErrors.amount = 'Amount cannot be negative.';
        } else if (parseFloat(appointmentData.amount) === 0) {
            newErrors.amount = 'Amount must be greater than zero.';
        }

        // Staff validation (#1222: validate staff assignment)
        if (!appointmentData.staffId || appointmentData.staffId <= 0) {
            newErrors.staffId = 'Please assign a staff member.';
        }

        // Payment Status validation
        if (!appointmentData.paymentStatus) {
            newErrors.paymentStatus = 'Please select a payment status.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCancel = () => {
        if (isDirty) {
            setShowCancelConfirmation(true);
        } else {
            onClose();
        }
    };

    const handleConfirmDiscard = () => {
        setShowCancelConfirmation(false);
        setIsDirty(false);
        onClose();
    };

    // Close the modal on Escape key press (routes through the same dirty-check)
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCancel();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isDirty]);

    const handleSubmit = async () => {
        try {
            if (!validateForm()) {
                return;
            }

            // PBI 1962 — with bookable option groups the amount is the derived
            // client total (display/legacy value — the server recomputes and
            // overrides it authoritatively), the legacy pricingOptionId is
            // best-effort matched from the chosen variant (dual-write scalar,
            // only load-bearing when the backend flag is OFF), and the two
            // additive option keys are included. Legacy services spread `{}`
            // so the request body stays byte-identical to today.
            const appointmentRequest = {
                clientName: appointmentData.clientName.trim(),
                mobileNumber: appointmentData.mobileNumber,
                email: appointmentData.email,
                categoryId: appointmentData.categoryId,
                serviceId: appointmentData.serviceId,
                pricingOptionId: activeOptionGroups
                    ? matchLegacyPricingOptionId(activeOptionGroups, optionSelection, pricingOptions)
                    : appointmentData.pricingOptionId,
                amount: activeOptionGroups
                    ? (optionTotals?.price ?? 0)
                    : parseFloat(appointmentData.amount),
                ...buildOptionSelectionPayload(activeOptionGroups, optionSelection),
                staffId: appointmentData.staffId,
                paymentStatus: appointmentData.paymentStatus,
                status: appointmentData.appointmentStatus,
                appointmentDate: appointmentData.appointmentDate,
                isDraft: appointmentData.isDraft,
                notes: appointmentData.notes
            };

            if (isEditMode && editingAppointmentId) {
                await updateAppointment(parseInt(editingAppointmentId), appointmentRequest);
                showToast('Appointment updated successfully.', 'success');
            } else {
                await createAppointment({
                    businessId: appointmentData.businessId,
                    ...appointmentRequest
                });
                if (appointmentData.isDraft) {
                    showToast('Appointment saved as draft successfully.', 'success');
                } else {
                    showToast('Appointment added successfully.', 'success');
                }
            }

            setIsDirty(false);
            onSuccess();
        } catch (error) {
            console.error('Error saving appointment:', error);
            showToast('Error adding appointment. Please try again later.', 'error');
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`}>
            <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                {(appointmentLoading || isInitialLoading) && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}>
                            <svg viewBox="0 0 50 50">
                                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                            </svg>
                            <span>
                                {isInitialLoading ? 'Loading appointment...' : (isEditMode ? 'Updating appointment...' : 'Creating appointment...')}
                            </span>
                        </div>
                    </div>
                )}

                <div className={styles.headerCloseContainer}>
                    <button onClick={handleCancel} className={styles.closeButton}>&times;</button>
                    <h2 className={styles.header}>
                        {isEditMode ? 'Edit appointment' : 'New appointment'}
                    </h2>
                </div>

                <div className={styles.appointmentForm}>
                    <InputField
                        label="Client name"
                        name="clientName"
                        value={appointmentData.clientName}
                        onChange={(value) => handleInputChange('clientName', (value as string).replace(/[<>&"']/g, ''))}
                        placeholder="Enter client name"
                        feedback={errors.clientName ? 'error' : undefined}
                        feedbackMessage={errors.clientName}
                    />

                    <InputField
                        label="Mobile number"
                        name="mobileNumber"
                        value={appointmentData.mobileNumber}
                        onChange={(value) => handleInputChange('mobileNumber', value)}
                        placeholder="Enter mobile number"
                        feedback={errors.mobileNumber ? 'error' : undefined}
                        feedbackMessage={errors.mobileNumber}
                    />

                    <InputField
                        label="Email address"
                        name="email"
                        value={appointmentData.email}
                        onChange={(value) => handleInputChange('email', value)}
                        type="email"
                        placeholder="Enter email address"
                        feedback={errors.email ? 'error' : undefined}
                        feedbackMessage={errors.email}
                    />

                    <Calendar
                        label="Appointment Date"
                        selectedDate={calendarSelectedDate}
                        onDateChange={handleCalendarDateChange}
                        value={calendarSelectedDate ? 'dateselected' : 'Select appointment date'}
                        leftAligned={false}
                        noBorderRadius={true}
                    />

                    <TimePicker
                        label="Appointment Time"
                        selectedTime={selectedTimeSlot}
                        onTimeChange={handleTimeChange}
                        value={selectedTimeSlot || 'Select appointment time'}
                        noBorderRadius={true}
                        feedback={errors.appointmentTime ? 'error' : undefined}
                        feedbackMessage={errors.appointmentTime}
                    />

                    <Dropdown
                        label="Category"
                        options={categories.map(category => category.name)}
                        value={getCategoryDisplayValue(appointmentData.categoryId)}
                        onChange={handleCategoryChange}
                        isLoading={categoriesLoading}
                        disabled={categoriesLoading}
                        feedback={errors.categoryId ? 'error' : undefined}
                        feedbackMessage={errors.categoryId}
                    />

                    <Dropdown
                        label="Service"
                        options={filteredServices.map(service => service.name)}
                        value={getServiceDisplayValue(appointmentData.serviceId)}
                        onChange={handleServiceChange}
                        isLoading={servicesLoading}
                        disabled={servicesLoading || appointmentData.categoryId <= 0 || filteredServices.length === 0}
                        feedback={errors.serviceId ? 'error' : undefined}
                        feedbackMessage={errors.serviceId}
                    />

                    {activeOptionGroups ? (
                        /* PBI 1962 — grouped option picker replaces the legacy
                           pricing-option dropdown; the amount is derived from
                           the selection (server-authoritative) and read-only. */
                        <>
                            <AppointmentOptionPicker
                                groups={activeOptionGroups}
                                selection={optionSelection}
                                onChange={handleOptionSelectionChange}
                                disabled={appointmentLoading || isInitialLoading}
                                showErrors={!!errors.optionSelection}
                            />
                            {errors.optionSelection && (
                                <p className={styles.optionSelectionError} role="alert">
                                    {errors.optionSelection}
                                </p>
                            )}
                            <InputField
                                label={t('appointments.options.total')}
                                name="amount"
                                value={optionTotals ? optionTotals.price.toFixed(2) : ''}
                                type="number"
                                unit={optionTotals?.currency ?? pricingOptions[0]?.currency ?? 'AED'}
                                disabled
                                dir="ltr"
                            />
                        </>
                    ) : (
                        <>
                            <Dropdown
                                label="Pricing Option"
                                options={pricingOptions.map(option => option.name)}
                                value={getPricingOptionDisplayValue(appointmentData.pricingOptionId)}
                                onChange={handlePricingOptionChange}
                                disabled={appointmentData.serviceId <= 0 || pricingOptions.length === 0}
                                feedback={errors.pricingOptionId ? 'error' : undefined}
                                feedbackMessage={errors.pricingOptionId}
                            />

                            <InputField
                                label="Amount"
                                name="amount"
                                value={appointmentData.amount}
                                onChange={(value) => handleInputChange('amount', value)}
                                type="number"
                                unit="AED"
                                placeholder="Enter amount"
                                feedback={errors.amount ? 'error' : undefined}
                                feedbackMessage={errors.amount}
                            />
                        </>
                    )}

                    <Dropdown
                        label="Assign to staff"
                        options={staff.map(s => s.fullName)}
                        value={getStaffDisplayValue(appointmentData.staffId)}
                        onChange={handleStaffChange}
                        isLoading={staffLoading}
                        disabled={staffLoading}
                        required
                        feedback={errors.staffId ? 'error' : undefined}
                        feedbackMessage={errors.staffId}
                    />

                    <Dropdown
                        label="Payment Status"
                        options={paymentStatusOptions.map(status => status.label)}
                        value={getPaymentStatusDisplayValue(appointmentData.paymentStatus)}
                        onChange={handlePaymentStatusChange}
                    />

                    <Dropdown
                        label="Appointment Status"
                        options={appointmentStatusOptions.map(status => status.label)}
                        value={getAppointmentStatusDisplayValue(appointmentData.appointmentStatus)}
                        onChange={handleAppointmentStatusChange}
                    />

                    <TextArea
                        label="Notes"
                        name="notes"
                        value={appointmentData.notes}
                        onChange={(value) => handleInputChange('notes', value)}
                        placeholder="Enter any additional notes..."
                    />

                    <Toggle
                        name="isDraft"
                        label="Save as draft"
                        checked={appointmentData.isDraft}
                        onChange={(value) => handleInputChange('isDraft', value)}
                    />

                    <div className={styles.buttonContainer}>
                        <Button
                            onClick={handleCancel}
                            label="Cancel"
                            size="medium"
                            noAppearance={true}
                            disabled={isInitialLoading || appointmentLoading}
                        />
                        <Button
                            onClick={handleSubmit}
                            label={isEditMode ? 'Update' : 'Add'}
                            size="medium"
                            disabled={servicesLoading || staffLoading || appointmentLoading || categoriesLoading || isInitialLoading}
                        />
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showCancelConfirmation}
                onClose={() => setShowCancelConfirmation(false)}
                onConfirm={handleConfirmDiscard}
                title="Discard Changes"
                message="Are you sure you want to discard this appointment? All entered details will be lost."
                confirmLabel="Discard"
                cancelLabel="Go Back"
                confirmColor="var(--destructive-fill)"
            />
        </div>
    );
};

export default AddEditAppointment;